/**
 * 项目设定自动生成 API 模块
 * 
 * 处理"一键创建剧本"功能的 API 调用
 * 
 * @module services/user/projectSettingGeneration.api
 * @see Requirements 2.1, 6.2, 6.7
 */

import { httpClient } from '../httpClient';
import {
  StartAutoGenerationRequest,
  StartAutoGenerationResponse,
  RetryStepRequest,
  AutoGenerationStatusResponse,
  GenerationStepType
} from '@packages/global';

const BASE_URL = '/user/project-setting-generation';

/**
 * 项目设定自动生成 API
 */
export class ProjectSettingGenerationApi {
  /**
   * 启动自动生成
   * 创建项目并启动自动生成流程
   * 
   * @param data - 包含剧本创意和可选的 LLM 模型 ID
   * @returns 项目 ID 和任务 ID
   * @see Requirements 1.2, 2.1
   */
  async startAutoGeneration(data: StartAutoGenerationRequest): Promise<StartAutoGenerationResponse> {
    return httpClient.post<StartAutoGenerationResponse>(`${BASE_URL}/start`, data);
  }

  /**
   * 重试失败步骤
   * 
   * @param projectId - 项目 ID
   * @param stepType - 要重试的步骤类型
   * @returns 新的任务 ID
   * @see Requirements 6.2, 6.3
   */
  async retryStep(projectId: string, stepType: GenerationStepType): Promise<{ jobId: string }> {
    const data: RetryStepRequest = { stepType };
    return httpClient.post<{ jobId: string }>(`${BASE_URL}/${projectId}/retry`, data);
  }

  /**
   * 取消自动生成
   * 
   * @param projectId - 项目 ID
   * @see Requirements 6.4
   */
  async cancelAutoGeneration(projectId: string): Promise<void> {
    return httpClient.post<void>(`${BASE_URL}/${projectId}/cancel`);
  }

  /**
   * 切换到手动模式
   * 
   * @param projectId - 项目 ID
   * @see Requirements 6.7, 6.8, 6.9
   */
  async switchToManualMode(projectId: string): Promise<void> {
    return httpClient.post<void>(`${BASE_URL}/${projectId}/manual-mode`);
  }

  /**
   * 获取自动生成状态
   * 
   * @param projectId - 项目 ID
   * @returns 自动生成状态
   * @see Requirements 9.2, 9.3
   */
  async getStatus(projectId: string): Promise<AutoGenerationStatusResponse> {
    return httpClient.get<AutoGenerationStatusResponse>(`${BASE_URL}/${projectId}/status`);
  }
}

// 导出单例实例
export const projectSettingGenerationApi = new ProjectSettingGenerationApi();
